/**
 * Kopfbereich der Willkommensseite.
 *
 * Sticky AppBar mit Logo, Anker-Links (Desktop) und den Handlungsaufrufen:
 * Sprach-Umschalter, Farbschema-Umschalter und Mitmachen_Aktion.
 * `component="header"` liefert den semantischen Kopfbereich (Req 11.4).
 */

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import ThemeModeSwitcher from "../../components/ThemeModeSwitcher";
import { useI18n } from "../../i18n/I18nProvider";
import { welcomeColors } from "./welcomeTheme";
import { JoinButton, WelcomeLogo, scrollToAnchor } from "./welcomeShared";

/** Anker-Links der Navigation (Desktop). */
const NAV_ITEMS = [
  { href: "#so-gehts", labelKey: "welcome.nav.howItWorks" },
  { href: "#vorteile", labelKey: "welcome.nav.benefits" },
  { href: "#fragen", labelKey: "welcome.nav.faq" },
] as const;

export default function WelcomeHeader() {
  const { t } = useI18n();
  const theme = useTheme();
  const colors = welcomeColors(theme.palette.mode);

  return (
    <AppBar
      component="header"
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        bgcolor: colors.headerBackground,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72, md: 96 },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <a href="#main" onClick={scrollToAnchor} style={{ display: "inline-flex" }}>
            <WelcomeLogo
              width={{ xs: 176, sm: 260, md: 340 }}
              src="/se-logo-appbar-hell.png"
              srcDark="/se-logo-appbar-dunkel.png"
            />
          </a>

          <Stack
            direction="row"
            spacing={2.5}
            sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
          >
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.href}
                color="inherit"
                href={item.href}
                onClick={scrollToAnchor}
                sx={{ minHeight: 44 }}
              >
                {t(item.labelKey)}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Sprach- und Farbschema-Umschalter der App wiederverwenden;
                Buttons per scoped sx auf 44 px Tap-Ziel heben (Req 11.1). */}
            <Box
              sx={{
                "& .MuiToggleButton-root": { minHeight: 44, minWidth: 44 },
              }}
            >
              <LanguageSwitcher />
            </Box>
            <ThemeModeSwitcher />
            {/* Navbar-CTA erst ab sm: auf Smartphones frisst der große
                Pill-Button den Platz fürs Logo; der Hero-CTA übernimmt dort. */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <JoinButton labelKey="welcome.nav.join" />
            </Box>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
